import * as React from 'react'
import { Link } from 'react-router'

import { moment } from 'features/Moment'
import { Container, Row, Col } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import HeadingBanner from 'components/Text/HeadingBanner'
import { Crumbs, Crumb } from 'components/BreadCrumbs'
import Button from 'components/Button'
import InfoCard from 'components/Cards/InfoCard'
import StickyChannel from 'components/StickyChannel'
import SimpleTable from 'components/Tables/SimpleTable'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import Tooltip from 'components/Tooltip'
import CasesTimeline from 'features/Cases/CasesTimeline'
import NumbersEdit from 'features/Numbers/NumbersEdit'
import { CallsTrendsVolumeContainer } from 'features/CallAnalytics/Cards/CallsTrendsVolume'
import { useGetPhoneLineQuery } from 'src/generated/graphql'
import { dispatchToProps } from './PhoneLine.container'

import cn from 'classnames'
import * as sText from 'components/Text/Text.scss'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as s from './PhoneLine.scss'

export interface IPhoneLineProps {
  params: {
    serviceId: string
  }
  location: {
    query: {
      page?: string
    }
  }
  showCasesCreateTroubleTicket: typeof dispatchToProps['showCasesCreateTroubleTicket']
  showNumbersDisconnectModal: typeof dispatchToProps['showNumbersDisconnectModal']
  children: React.ReactNode
}

export const PhoneLineComponent = (props: IPhoneLineProps) => {
  const { params, children, showCasesCreateTroubleTicket, showNumbersDisconnectModal } = props

  const { data, loading } = useGetPhoneLineQuery({
    variables: { id: params.serviceId },
  })

  if (loading || !data) {
    return (
      <Page layout={{ header: 'app' }} title="Phone Lines">
        <LoadingOverlay transparent />
      </Page>
    )
  }
  const service = data.phoneLine
  const inactive = service.billingStatus !== 'Active'
  const inactiveMessage = inactive ? 'This service is not active' : ''

  return (
    <Page layout={{ header: 'app' }} title="Phone Lines">
      <PageHeader>
        <Crumbs title="Phone Lines">
          <Crumb parent to="/dashboard/phone-lines">
            Phone Lines
          </Crumb>
          <Crumb
            active={!location.pathname.includes('/usage')}
            to={`/dashboard/phone-lines/${service.id}`}
          >
            {service.number}
          </Crumb>
          <Crumb
            active={location.pathname.includes('/usage')}
            to={`/dashboard/phone-lines/${service.id}/usage`}
          >
            Usage
          </Crumb>
        </Crumbs>
        <HeadingBanner>
          <h3 className={cn(sText.heading3, sText.accent)}>Phone Line: {service.number}</h3>
        </HeadingBanner>
      </PageHeader>
      <Container className="md">
        <div className={s.formSection}>
          {children || (
            <div>
              <CallsTrendsVolumeContainer numberId={service.id} />
              <Row className={cn(sLay['layout-xs-column'], sLay['layout-gt-xs-row'], 'md')}>
                <Col className={cn('md', sLay['flex-gt-xs-60'])}>
                  <InfoCard className={sMarg.mb30to15}>
                    <NumbersEdit service={service} />
                  </InfoCard>
                  <InfoCard title="Billing Details" className={sMarg.mb30to15}>
                    <SimpleTable>
                      <div>
                        <div>Billing Account:</div>
                        <div>
                          <Link to={`/dashboard/billing/accounts/${service.ban}`}>
                            {service.ban}
                          </Link>
                        </div>
                      </div>
                      <div>
                        <div>Billing Status:</div>
                        <div>{service.billingStatus}</div>
                      </div>
                      {service.usagePlan && (
                        <div>
                          <div>Usage Plan: </div>
                          <div>{service.usagePlan}</div>
                        </div>
                      )}
                      {service.billingStartDate && (
                        <div>
                          <div>Billing Start Date:</div>
                          <div>{moment(service.billingStartDate).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      {service.billingEndDate && (
                        <div>
                          <div>Billing End Date:</div>
                          <div>{moment(service.billingEndDate).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                    </SimpleTable>
                  </InfoCard>
                  <InfoCard title="Line Details" className={sMarg.mb30to15}>
                    <SimpleTable>
                      <div>
                        <div>Number:</div>
                        <div>{service.number}</div>
                      </div>
                      {service.serviceType && (
                        <div>
                          <div>Service Type: </div>
                          <div>{service.serviceType}</div>
                        </div>
                      )}
                      {service.currentStatus && (
                        <div>
                          <div>Curent Status: </div>
                          <div>{service.currentStatus}</div>
                        </div>
                      )}
                      {service.contractEnd && (
                        <div>
                          <div>Contract End Date:</div>
                          <div>{moment(service.contractEnd).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      {service.cpeType && (
                        <div>
                          <div>Line Details: </div>
                          <div>{service.cpeType}</div>
                        </div>
                      )}
                      {service.ntdSerial && (
                        <div>
                          <div>NTD Serial: </div>
                          <div>{service.ntdSerial}</div>
                        </div>
                      )}
                      {service.ntuPort && (
                        <div>
                          <div>NTU Port: </div>
                          <div>{service.ntuPort}</div>
                        </div>
                      )}
                      {service.simNumber && (
                        <div>
                          <div>Sim Number: </div>
                          <div>{service.simNumber}</div>
                        </div>
                      )}
                      {service.nbnDisconnectionDate && (
                        <div>
                          <div>NBN Disconnection Date:</div>
                          <div>{moment(service.nbnDisconnectionDate).format('DD MMM YYYY')}</div>
                        </div>
                      )}
                      {service.nbnLocationId && (
                        <div>
                          <div>NBN Location ID: </div>
                          <div>{service.nbnLocationId}</div>
                        </div>
                      )}
                      {service.lastBatteryReplacementDate && (
                        <div>
                          <div>Last Battery Replacement Date:</div>
                          <div>
                            {moment(service.lastBatteryReplacementDate).format('DD MMM YYYY')}
                          </div>
                        </div>
                      )}
                      {service.description && (
                        <div>
                          <div>Description: </div>
                          <div>{service.description}</div>
                        </div>
                      )}
                    </SimpleTable>
                  </InfoCard>
                </Col>
                <Col className={cn(sLay['flex-gt-xs-40'], 'md')}>
                  <StickyChannel>
                    <div className={sLay['layout-column']}>
                      <Button
                        onClick={() =>
                          showCasesCreateTroubleTicket({
                            serviceId: service.id,
                            type: 'phoneLine',
                          })
                        }
                        className={sMarg.mb15}
                        color="secondary"
                        size="sm"
                        rect
                        disabled={inactive}
                        title={inactiveMessage}
                      >
                        Submit a Trouble Ticket
                      </Button>
                      {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                      <Button
                        onClick={() =>
                          showNumbersDisconnectModal({
                            serviceId: service.id,
                            type: 'phoneLine',
                          })
                        }
                        className={sMarg.mb15}
                        color="warn"
                        size="sm"
                        rect
                        disabled={inactive}
                        title={inactiveMessage}
                      >
                        Disconnect
                      </Button>
                      {inactiveMessage && <Tooltip>{inactiveMessage}</Tooltip>}
                    </div>
                    <CasesTimeline serviceId={service.id} />
                  </StickyChannel>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Container>
    </Page>
  )
}



// WEBPACK FOOTER //
// src/pages/PhoneLine/PhoneLine.tsx